import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, apiPut } from "../api";
import type { CursorPage, Datified, Schemas } from "../../api/types";
import { POLL_BASE_MS } from "./intervals";
import { qs } from "./query";

export type DocumentType = Schemas["DocumentType"];
export type DocumentStatus = Schemas["DocumentStatus"];
export type PaymentType = Schemas["PaymentType"];
export type DocumentSummary = Schemas["DocumentSummary"];
export type Document = Datified<Schemas["Document"], "issued_at" | "created_at" | "updated_at">;
export type DocumentInput = Schemas["DocumentInput"];
export type DocumentCreate = Schemas["DocumentCreate"];
export type DocumentItemInput = Schemas["DocumentItemInput"];
export type Totals = Schemas["Totals"];

export const DOC_TYPE_LABEL: Record<DocumentType, string> = {
  sales_invoice: "Faktura",
  purchase_invoice: "Přijatá faktura",
  credit_note: "Dobropis",
  sales_order: "Cenová nabídka",
  retail: "Prodejka",
};

/** Plural headings for the list pages. */
export const DOC_TYPE_PLURAL: Record<DocumentType, string> = {
  sales_invoice: "Faktury",
  purchase_invoice: "Přijaté faktury",
  credit_note: "Dobropisy",
  sales_order: "Cenové nabídky",
  retail: "Prodejky",
};

export const PAYMENT_LABEL: Record<PaymentType, string> = {
  bank: "Převodem",
  cash: "Hotově",
  card: "Platební kartou",
};

export interface DocumentFilters {
  type?: DocumentType;
  status?: DocumentStatus;
  year?: number;
  q?: string;
  cursor?: string;
  limit?: number;
}

export function useDocumentList(companyId: string) {
  return function useList(filters: DocumentFilters = {}) {
    return useQuery<CursorPage<DocumentSummary>>({
      queryKey: ["companies", companyId, "documents", "list", filters],
      queryFn: () =>
        apiGet<CursorPage<DocumentSummary>>(`/companies/${companyId}/documents${qs({ ...filters })}`),
      refetchInterval: POLL_BASE_MS,
    });
  };
}

/** A new document's content copied from an existing one; nothing is stored. */
export function useDocumentCopy(companyId: string, fromId: string | null) {
  return useQuery<DocumentCreate>({
    queryKey: ["companies", companyId, "documents", "copy", fromId],
    queryFn: () => apiGet<DocumentCreate>(`/companies/${companyId}/documents/${fromId}/copy`),
    enabled: !!fromId,
    // Fetched once per visit (the editor remounts per source): a copy is
    // dated today, and a refetch on focus would only reset — or, failing,
    // unmount — the form being filled in.
    gcTime: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function copyPath(companyId: string, fromId: string): string {
  return `/c/${companyId}/documents/new?from=${fromId}`;
}

export function useDocument(companyId: string, id: string | undefined) {
  return useQuery<Document>({
    queryKey: ["companies", companyId, "documents", "one", id],
    queryFn: () => apiGet<Document>(`/companies/${companyId}/documents/${id}`),
    enabled: !!id,
  });
}

function useInvalidate(companyId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["companies", companyId, "documents"] });
    void qc.invalidateQueries({ queryKey: ["companies", companyId, "series"] });
    void qc.invalidateQueries({ queryKey: ["companies", companyId, "next-number"] });
  };
}

export function useSaveDocument(companyId: string) {
  const invalidate = useInvalidate(companyId);
  return useMutation<Document, unknown, { id?: string; input: DocumentCreate }>({
    meta: { suppressErrorToast: true },
    mutationFn: ({ id, input }) => {
      if (id) {
        const { type: _type, ...body } = input;
        void _type;
        return apiPut<Document>(`/companies/${companyId}/documents/${id}`, body);
      }
      return apiPost<Document>(`/companies/${companyId}/documents`, input);
    },
    onSuccess: invalidate,
  });
}

export function useIssueDocument(companyId: string) {
  const invalidate = useInvalidate(companyId);
  return useMutation<Document, unknown, { id: string; number?: string }>({
    meta: { suppressErrorToast: true },
    mutationFn: ({ id, number }) =>
      apiPost<Document>(`/companies/${companyId}/documents/${id}/issue`, number ? { number } : {}),
    onSuccess: invalidate,
  });
}

export function useDeleteDocument(companyId: string) {
  const invalidate = useInvalidate(companyId);
  return useMutation<void, unknown, string>({
    mutationFn: async (id) => {
      await apiDelete(`/companies/${companyId}/documents/${id}`);
    },
    onSuccess: invalidate,
  });
}

export function useCreditNote(companyId: string) {
  const invalidate = useInvalidate(companyId);
  return useMutation<Document, unknown, { id: string; reason: string }>({
    meta: { suppressErrorToast: true },
    mutationFn: ({ id, reason }) =>
      apiPost<Document>(`/companies/${companyId}/documents/${id}/credit-note`, { reason }),
    onSuccess: invalidate,
  });
}

export function useConvertDocument(companyId: string) {
  const invalidate = useInvalidate(companyId);
  return useMutation<Document, unknown, string>({
    mutationFn: (id) => apiPost<Document>(`/companies/${companyId}/documents/${id}/convert`, {}),
    onSuccess: invalidate,
  });
}

/** Live totals for the editor, from the server's one implementation. */
export function previewTotals(companyId: string, items: DocumentItemInput[]): Promise<Totals> {
  return apiPost<Totals>(`/companies/${companyId}/totals-preview`, { items });
}

/** The list page of each type (its own path, so the sidebar can mark it). */
export const DOC_TYPE_SLUG: Record<DocumentType, string> = {
  sales_invoice: "invoices",
  purchase_invoice: "received-invoices",
  credit_note: "credit-notes",
  sales_order: "quotes",
  retail: "receipts",
};

export function listPath(companyId: string, type: DocumentType): string {
  return `/c/${companyId}/${DOC_TYPE_SLUG[type]}`;
}

export function documentPath(companyId: string, id: string): string {
  return `/c/${companyId}/documents/${id}`;
}

/** The HTML render, for the preview frame and printing. */
export function documentHtmlUrl(apiBase: string, companyId: string, id: string): string {
  return `${apiBase}/companies/${companyId}/documents/${id}/html`;
}
